import { UsageIcon } from "@/components/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { cn } from "cn";

import { errorMessage } from "../lib/query.ts";

import { USAGE_TEXTS as TEXTS } from "./texts.ts";
import {
  describeCost,
  formatTokens,
  totalTokens,
  UNKNOWN_PRICE,
  usageSummaryQuery,
} from "./usage.ts";
import { UsageBreakdown } from "./UsageBreakdown.tsx";

const MONTH = { range: "month" } as const;
const TODAY = { range: "today" } as const;

/**
 * El consumo del mes, arriba a la derecha y SIEMPRE a la vista (2.4.3).
 *
 * Lo monta la cabecera y no cada armazón, por lo mismo que el conmutador:
 * es un mueble fijo, y dos copias se separan al primer retoque. Se refresca al
 * terminar cada turno --lo invalida `AppShell`, que es quien se entera-- y
 * lleva a la vista de consumo de Work, que es donde se mira con calma.
 *
 * Tiene que caber sin empujar al conmutador: en poco ancho se queda en el
 * icono y el coste, y los tokens solo aparecen cuando sobra sitio.
 */
export function UsageMeter() {
  const month = useQuery(usageSummaryQuery(MONTH));
  const today = useQuery(usageSummaryQuery(TODAY));

  // Con `?.` también por dentro: una respuesta sin `totals` --un backend a
  // medias-- deja el contador en "—", no la cabecera entera en blanco.
  const totals = month.data?.totals;
  const todayTotals = today.data?.totals;
  const cost =
    totals === undefined
      ? null
      : describeCost(totals.costUsd, totals.unpricedCalls);

  const label =
    totals === undefined || cost === null
      ? month.isPending
        ? "…"
        : "—"
      : cost.kind === "unknown"
        ? TEXTS.meter.tokens(formatTokens(totalTokens(totals)))
        : cost.short;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/work/consumo"
          search={(previous) => previous}
          className={cn(
            "flex h-8 min-w-0 shrink items-center gap-1.5 rounded-md px-2 text-sm whitespace-nowrap tabular-nums",
            "text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
            cost !== null && cost.kind !== "estimated" && "text-warning",
          )}
          aria-label={TEXTS.meter.ariaLabel(label)}
        >
          <UsageIcon className="shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          {totals !== undefined && (
            <span className="hidden text-xs xl:inline">
              ·{" "}
              {cost?.kind === "unknown"
                ? UNKNOWN_PRICE
                : TEXTS.meter.tokens(formatTokens(totalTokens(totals)))}
            </span>
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="grid gap-2">
        {month.isError ? (
          <p>{TEXTS.meter.error(errorMessage(month.error))}</p>
        ) : totals === undefined ? (
          <p>{month.isPending ? TEXTS.meter.reading : TEXTS.meter.noData}</p>
        ) : (
          <UsageBreakdown usage={totals} title={monthTitle()} />
        )}

        {todayTotals !== undefined && (
          <p className="border-t pt-2">
            {TEXTS.meter.today(formatTokens(totalTokens(todayTotals)))}
            {
              describeCost(todayTotals.costUsd, todayTotals.unpricedCalls)
                .medium
            }
          </p>
        )}

        <p className="text-muted-foreground">{TEXTS.meter.estimated}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const monthTitle = (): string => {
  const name = new Date().toLocaleDateString("es", { month: "long" });
  return TEXTS.meter.month(name);
};
